import {
  Autocomplete,
  type AutocompleteRenderInputParams,
  Checkbox,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  type PlaceNode,
  PlaceNodeTypeEnum,
} from '@ulysses-inc/harami_api_client'
import { type FC, useCallback, useMemo, useRef, useState } from 'react'
import { useQueryAssignablePlaces } from 'src/features/places/api/useQueryAssignablePlaces'
import { flattenNodes } from './EditPlaceGroup'
import type { useFormikProps } from './EditPlaceGroup.hooks'

type TProps = Pick<ReturnType<typeof useFormikProps>, 'setFieldValue'> & {
  nodes: PlaceNode[]
  activeNodeId?: string
  activeParentNodeId?: number
}

// debounce の時間
const DEBOUNCE_TIMEOUT = 500

const PlacesRow: FC<TProps> = ({
  setFieldValue,
  nodes,
  activeNodeId,
  activeParentNodeId,
}) => {
  const placeGroupNodes = flattenNodes(nodes, []).filter(
    ({ type }) => type === PlaceNodeTypeEnum.PlaceGroup,
  )
  const activeNode = placeGroupNodes.find(({ uuid }) => uuid === activeNodeId)
  const activeParentNode = placeGroupNodes.find(
    ({ id }) => id === activeParentNodeId,
  )
  // 編集対象（新規作成時は親）グループの UUID。
  // この系統（最上位ツリー全体）に所属済みの現場をサーバー側で除外する。
  // 新規ルート直下グループの場合は両方 undefined になり、除外なしで全件候補になる。
  const targetGroupNodeUuid = (activeNode ?? activeParentNode)?.uuid

  // 編集対象グループに現在所属している現場。
  // サーバーは除外対象として返さないため、チェック済みで表示し外せるよう手元でマージする。
  const activePlaceNodes = useMemo(
    () =>
      ((activeNode?.nodes as PlaceNode[]) || []).filter(
        ({ type }) => type === PlaceNodeTypeEnum.Place,
      ),
    [activeNode?.nodes],
  )

  const [value, setValue] = useState<PlaceNode[]>(activePlaceNodes)

  // 検索文字列（debounce 後にサーバーへ渡る placeName）
  const [placeName, setPlaceName] = useState('')
  const [isSearching, setIsSearching] = useState(false)

  const { data, isLoading } = useQueryAssignablePlaces({
    targetGroupNodeUuid,
    placeName,
  })

  // サーバーが除外済みの候補。所属済み（activePlaceNodes）はチェック済みで外せるようマージする。
  const placeNodes = useMemo(() => {
    const fetchedPlaceNodes = (data?.placeNodes ?? []).filter(
      ({ type }) => type === PlaceNodeTypeEnum.Place,
    )
    const merged = [...fetchedPlaceNodes]
    activePlaceNodes.forEach(activePlaceNode => {
      if (!merged.some(node => node.id === activePlaceNode.id)) {
        merged.push(activePlaceNode)
      }
    })
    return merged.sort((a, b) => (a.id < b.id ? -1 : 1))
  }, [data?.placeNodes, activePlaceNodes])

  // 上限を無視した（除外後の）選択可能な総現場数
  const totalPlacesCount = data?.count ?? 0
  // 実際に返ってきた候補数（活性ノードのマージ前 = サーバー返却分）
  const fetchedPlacesCount = data?.placeNodes?.length ?? 0

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const debounceFetch = useCallback((text: string) => {
    if (timer.current) {
      clearTimeout(timer.current)
    }
    timer.current = setTimeout(() => {
      setPlaceName(text)
    }, DEBOUNCE_TIMEOUT)
  }, [])

  const clearSearch = useCallback(() => {
    // 未実行のデバウンス検索をキャンセルしてからリセット
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
    setPlaceName('')
  }, [])

  const onChange = (nodeIds: string[]) => {
    setFieldValue('nodeIds', nodeIds)
    // 検索フィルタがかかっている場合のみリセットする
    if (isSearching) {
      clearSearch()
      setIsSearching(false)
    }
  }
  const onBlur = () => {
    clearSearch()
    // フォーカスアウト時に検索中フラグも戻し、infoText 表示や onChange 側の状態と揃える
    setIsSearching(false)
  }
  // 取得件数が、除外後に選択可能な総現場数より少なければ上限到達の文言を表示
  // 検索中・ローディング中は件数が変動するためメッセージを非表示にする
  const infoText =
    !isSearching && !isLoading && fetchedPlacesCount < totalPlacesCount
      ? `表示されていない現場への紐付けに関しましては、サポートまでお問い合わせください。`
      : ''

  return (
    <Stack>
      <Typography variant="subtitle1" mb={1}>
        所属させる現場
      </Typography>
      <Typography variant="caption">{infoText}</Typography>
      <Autocomplete<PlaceNode, true, false, false>
        multiple
        disableCloseOnSelect
        loading={isLoading}
        loadingText=""
        options={placeNodes}
        getOptionLabel={option => option.place?.name ?? '名称未設定'}
        value={value}
        onChange={(_, newValue) => {
          // AutoComplete に使用するフロントエンドのデータ
          setValue(newValue)
          // API に送信するバックエンド用のデータ
          onChange(newValue.map(({ id }) => id.toString()))
        }}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        renderOption={(props, option, { selected }) => {
          const { key, ...rest } = props
          return (
            <li key={key} {...rest}>
              <Stack direction="row" alignItems="center" spacing={1} mr={1}>
                <Checkbox size="small" sx={{ mr: 1 }} checked={selected} />
                {option.place?.name}
              </Stack>
            </li>
          )
        }}
        onBlur={onBlur}
        onInputChange={(_, value, reason) => {
          const hasInput = value !== ''
          setIsSearching(hasInput)

          if (reason === 'input') {
            debounceFetch(value)
          } else if (!hasInput && reason === 'clear') {
            // クリアボタンで入力が消えた場合のみリセット
            // reason === 'reset'(選択後)は onChange 側の clearSearch() が担当するため除外
            clearSearch()
          }
        }}
        slotProps={{
          chip: {
            size: 'small',
          },
        }}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextField {...params} />
        )}
      />
    </Stack>
  )
}

export default PlacesRow
